import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { TableCell, TableRow } from '@tempmee/react';
import { promoCodePath } from '../paths';
import { PromoCodeStatusLabel } from './PromoCodeStatusLabel';
import { PromoCodeTableCard } from './PromoCodeTableCard';

// The ticket's columns plus Code, which the Figma shows beside Name — ops know
// a promotion by either. "Max Uses" is the per-office cap — see mapPromoCode
// for why; the campaign-wide cap is on the detail page.
const COLUMNS = [
  'Name',
  'Code',
  'Discount',
  'Start Date',
  'End Date',
  'Status',
  'Max Uses',
  'Auto-apply',
  'Actions',
];

/**
 * The Promo Codes list, in the shared `PromoCodeTableCard` frame.
 *
 * `title={null}` (the frame's default) for the reason GotuAdminsTable gives:
 * the page header already reads "Promo Codes".
 */
export const PromoCodesTable = ({
  promoCodes, isPending, isError, hasFilters, footer,
}) => (
  <PromoCodeTableCard
    columns={COLUMNS}
    rowCount={promoCodes.length}
    isPending={isPending}
    isError={isError}
    emptyMessage={hasFilters ? 'No promo codes match these filters.' : 'No promo codes yet.'}
    errorMessage="Could not load promo codes."
    footer={footer}
  >
    {promoCodes.map((row) => (
      <TableRow key={row.id} hover>
        <TableCell>{row.name}</TableCell>
        {/* Codes have no spaces, so a long one would otherwise widen the table
            until the right-hand columns scroll off. QA has a 60-character code
            on dev for exactly this. */}
        <TableCell className="break-all">{row.code}</TableCell>
        <TableCell>{row.discountLabel}</TableCell>
        <TableCell>{row.startDateLabel}</TableCell>
        <TableCell>{row.endDateLabel}</TableCell>
        <TableCell>
          <PromoCodeStatusLabel status={row.status} label={row.statusLabel} />
        </TableCell>
        <TableCell>{row.maxUsesLabel}</TableCell>
        <TableCell>{row.autoApplyLabel}</TableCell>
        <TableCell>
          <RouterLink to={promoCodePath(row.id)}>View Code</RouterLink>
        </TableCell>
      </TableRow>
    ))}
  </PromoCodeTableCard>
);

PromoCodesTable.propTypes = {
  promoCodes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      code: PropTypes.string.isRequired,
      discountLabel: PropTypes.string.isRequired,
      startDateLabel: PropTypes.string.isRequired,
      endDateLabel: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      statusLabel: PropTypes.string.isRequired,
      maxUsesLabel: PropTypes.string.isRequired,
      autoApplyLabel: PropTypes.string.isRequired,
    }),
  ).isRequired,
  isPending: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
  /** Picks the empty copy: "nothing matched" reads as a bug with no filters set. */
  hasFilters: PropTypes.bool,
  /** Footer slot — the page passes its pagination. */
  footer: PropTypes.node,
};

PromoCodesTable.defaultProps = { hasFilters: false, footer: null };

export default PromoCodesTable;
